// theme.js - Fixed version that matches your working structure
export const tokensDark = {
  grey: {
    0: "#FFFFFF",
    10: "#F6F7FB",
    50: "#EFF1F6",
    100: "#E1E5EE",
    200: "#C9D0DE",
    300: "#AAB6CA",
    400: "#8C9BB1",
    500: "#6F8098",
    600: "#556477",
    700: "#3E4A59",
    800: "#283241",
    900: "#161C24",
    1000: "#0C1016",
  },
  // Deep navy primary
  primary: {
    100: "#E6EBFF",
    150: "#CBD6FF",
    200: "#A9BDFF",
    250: "#89A7FF",
    300: "#6A8FF7",
    350: "#4D71EB",
    400: "#3759D1",
    450: "#2C47A6",
    500: "#23387F",
    600: "#1A2B63",
    700: "#14214D",
    800: "#0E1838",
    900: "#0A122A",
  },
  // Teal accent secondary
  secondary: {
    50: "#E8FBF6",
    100: "#D0F7ED",
    200: "#A7EEDD",
    300: "#74E0C7",
    400: "#3FCFB0",
    500: "#17B89A",
    600: "#13947E",
    700: "#0F7161",
    800: "#0B5146",
    900: "#07332D",
  },
};

// 2) Reverse helper → light tokens auto
function reverseTokens(tokensDark) {
  const reversedTokens = {};
  Object.entries(tokensDark).forEach(([key, val]) => {
    const keys = Object.keys(val);
    const values = Object.values(val).reverse();
    const obj = {};
    keys.forEach((k, i) => (obj[k] = values[i]));
    reversedTokens[key] = obj;
  });
  return reversedTokens;
}
export const tokensLight = reverseTokens(tokensDark);

// 3) MUI theme settings
export const themeSettings = (mode) => {
  const isDark = mode === "dark";
  const t = isDark ? tokensDark : tokensLight;

  return {
    palette: {
      mode,
      ...(isDark
        ? {
            // Dark mode — deeper surfaces + teal accents
            primary: {
              ...t.primary,
              main: t.primary[350],
              light: t.primary[300],
              dark: t.primary[600],
            },
            secondary: {
              ...t.secondary,
              main: t.secondary[400],
              light: t.secondary[300],
              dark: t.secondary[600],
              accent: t.secondary[200],
              contrast: t.primary[600],
            },
            neutral: {
              ...t.grey,
              main: t.grey[500],
              light: t.grey[400],
            },
            chart: {
              ...t.secondary,
              main: t.secondary[300],
              light: t.secondary[500],
              dark: t.secondary[200],
            },
            background: {
              default: t.primary[800], // overall page
              alt: t.primary[700], // cards/toolbars
            },
            text: {
              primary: t.grey[10],
              secondary: t.grey[300],
              disabled: t.grey[500],
            },
            divider: t.grey[700],
          }
        : {
            // Light mode stays clean + compact
            primary: {
              ...t.primary,
              main: tokensDark.grey[50],
              light: tokensDark.grey[100],
            },
            secondary: {
              ...t.secondary,
              main: tokensDark.primary[500],
              light: tokensDark.primary[700],
              dark: tokensDark.primary[600],
              accent: tokensDark.primary[400],
              contrast: tokensDark.secondary[600],
            },
            neutral: {
              ...t.grey,
              main: tokensDark.grey[500],
              light: tokensDark.grey[400],
            },
            chart: {
              ...tokensDark.secondary,
              main: tokensDark.primary[400],
              light: tokensDark.primary[300],
              dark: tokensDark.primary[500],
            },
            background: {
              default: tokensDark.grey[0],
              alt: tokensDark.grey[50],
            },
            text: {
              primary: tokensDark.grey[900],
              secondary: tokensDark.grey[600],
              disabled: tokensDark.grey[400],
            },
            divider: tokensDark.grey[200],
          }),
    },

    // 4) Compact typography (slightly smaller everywhere)
    typography: {
      fontFamily: ["Poppins", "sans-serif"].join(","),
      fontSize: 11, // ↓ from 12
      h1: { fontFamily: "Poppins, sans-serif", fontSize: 34, fontWeight: 700 },
      h2: { fontFamily: "Poppins, sans-serif", fontSize: 28, fontWeight: 700 },
      h3: { fontFamily: "Poppins, sans-serif", fontSize: 22, fontWeight: 600 },
      h4: { fontFamily: "Poppins, sans-serif", fontSize: 18, fontWeight: 600 },
      h5: { fontFamily: "Poppins, sans-serif", fontSize: 14, fontWeight: 600 },
      h6: { fontFamily: "Poppins, sans-serif", fontSize: 13, fontWeight: 600 },
      body1: { fontSize: 13, lineHeight: 1.5 },
      body2: { fontSize: 12, lineHeight: 1.45 },
      button: { textTransform: "none", fontWeight: 600, letterSpacing: 0.2 },
    },

    // 5) Subtle rounding + lighter shadows → less bulky
    shape: { borderRadius: 10 },

    components: {
      // Global density tweaks
      MuiButton: {
        defaultProps: { size: "small" }, // compact
        styleOverrides: {
          root: {
            paddingInline: 12,
            paddingBlock: 7,
            borderRadius: 10,
            transition: "all 0.3s ease-in-out",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: isDark
                ? "0 8px 25px rgba(63, 207, 176, 0.15)"
                : "0 8px 25px rgba(55, 89, 209, 0.15)",
            },
          },
        },
        variants: [
          {
            props: { variant: "contained", color: "primary" },
            style: {
              background: isDark
                ? `linear-gradient(135deg, ${t.primary[350]} 0%, ${t.primary[600]} 100%)`
                : `linear-gradient(135deg, ${tokensDark.grey[50]} 0%, ${tokensDark.grey[100]} 100%)`,
              color: isDark ? "#ffffff" : tokensDark.primary[500],
              boxShadow: isDark
                ? "0 4px 14px 0 rgba(77, 113, 235, 0.3)"
                : "0 4px 14px 0 rgba(55, 89, 209, 0.2)",
            },
          },
          {
            props: { variant: "contained", color: "secondary" },
            style: {
              background: isDark
                ? `linear-gradient(135deg, ${t.secondary[400]} 0%, ${t.secondary[600]} 100%)`
                : `linear-gradient(135deg, ${tokensDark.primary[500]} 0%, ${tokensDark.primary[700]} 100%)`,
              color: isDark ? t.primary[600] : "#ffffff",
              boxShadow: isDark
                ? "0 4px 14px 0 rgba(63, 207, 176, 0.3)"
                : "0 4px 14px 0 rgba(35, 56, 127, 0.2)",
            },
          },
        ],
      },
      MuiTextField: {
        defaultProps: { size: "small" }, // compact inputs
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? t.grey[600] : t.grey[300],
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? t.secondary[400] : t.primary[450],
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? t.secondary[400] : t.primary[450],
              boxShadow: isDark
                ? "0 0 0 2px rgba(63, 207, 176, 0.15)"
                : "0 0 0 2px rgba(55, 89, 209, 0.15)",
            },
          },
          input: { paddingTop: 9, paddingBottom: 9 }, // a touch slimmer
        },
      },
      MuiListItem: {
        defaultProps: { dense: true }, // compact nav lists
      },
      MuiMenuItem: {
        defaultProps: { dense: true },
      },
      MuiTable: {
        defaultProps: { size: "small" }, // compact tables
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundImage: "none",
            boxShadow: isDark
              ? "0 8px 18px rgba(0,0,0,0.25)"
              : "0 8px 18px rgba(0,0,0,0.08)",
            border: `1px solid ${isDark ? t.grey[700] : t.grey[100]}`,
            transition: "all 0.3s ease-in-out",
          },
        },
      },
      MuiAppBar: {
        defaultProps: { color: "transparent", elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: "none",
            borderBottom: `1px solid ${isDark ? t.grey[700] : t.grey[200]}`,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            width: "100%",
            overflowX: "hidden",
            backgroundAttachment: "fixed",
            backgroundImage: isDark
              ? "linear-gradient(180deg, rgba(14,24,56,0.35) 0%, rgba(10,18,42,0) 55%)"
              : "none",
            scrollBehavior: "smooth",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: isDark ? t.primary[800] : t.grey[100],
            },
            "&::-webkit-scrollbar-thumb": {
              background: isDark ? t.primary[500] : t.primary[400],
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: isDark ? t.primary[400] : t.primary[500],
            },
          },
          "::selection": {
            background: isDark ? t.secondary[500] : tokensDark.primary[150],
            color: isDark ? "#0A122A" : "#0A122A",
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 8,
            fontSize: 11,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            transition: "all 0.3s ease-in-out",
            background: isDark
              ? `linear-gradient(135deg, ${t.primary[800]} 0%, ${t.primary[900]} 100%)`
              : `linear-gradient(135deg, #ffffff 0%, ${t.grey[50]} 100%)`,
            boxShadow: isDark
              ? "0 4px 25px rgba(0, 0, 0, 0.3)"
              : "0 4px 25px rgba(0, 0, 0, 0.08)",
            border: isDark
              ? `1px solid ${t.primary[700]}`
              : `1px solid ${t.grey[200]}`,
            "&:hover": {
              transform: "translateY(-5px)",
              boxShadow: isDark
                ? "0 12px 40px rgba(0, 0, 0, 0.4)"
                : "0 12px 40px rgba(0, 0, 0, 0.15)",
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1rem",
            "&.Mui-selected": {
              color: isDark ? t.secondary[400] : t.secondary[600],
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: isDark ? t.secondary[400] : t.secondary[600],
            height: 3,
            borderRadius: 2,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
        },
        variants: [
          {
            props: { color: "primary" },
            style: {
              background: isDark
                ? `linear-gradient(135deg, ${t.primary[400]} 0%, ${t.primary[600]} 100%)`
                : `linear-gradient(135deg, ${t.primary[500]} 0%, ${t.primary[700]} 100%)`,
              color: "white",
            },
          },
          {
            props: { color: "secondary" },
            style: {
              background: isDark
                ? `linear-gradient(135deg, ${t.secondary[400]} 0%, ${t.secondary[600]} 100%)`
                : `linear-gradient(135deg, ${t.secondary[500]} 0%, ${t.secondary[700]} 100%)`,
              color: isDark ? t.grey[900] : "white",
            },
          },
        ],
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            background: "transparent",
            boxShadow: "none",
            border: isDark
              ? `1px solid ${t.primary[700]}`
              : `1px solid ${t.grey[200]}`,
            borderRadius: "12px !important",
            marginBottom: "8px",
            "&:before": {
              display: "none",
            },
            "&.Mui-expanded": {
              margin: "8px 0",
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            "&.Mui-expanded": {
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 20,
            background: isDark
              ? `linear-gradient(135deg, ${t.primary[800]} 0%, ${t.primary[900]} 100%)`
              : `linear-gradient(135deg, #ffffff 0%, ${t.grey[50]} 100%)`,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            border: isDark
              ? `0px solid ${t.primary[600]}`
              : `0px solid ${t.grey[200]}`,
          },
        },
      },
      MuiCardMedia: {
        styleOverrides: {
          root: {
            transition: "transform 0.3s ease-in-out",
          },
        },
      },
      // Added: Checkbox overrides for DataGrid selection visibility
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: isDark ? t.grey[500] : t.grey[400],
            "&.Mui-checked": {
              color: isDark ? t.primary[300] : t.primary[500],
            },
            "&.MuiCheckbox-indeterminate": {
              color: isDark ? t.primary[300] : t.primary[500],
            },
          },
        },
      },
    },
  };
};

// Animation variants for consistent usage
export const motionVariants = {
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  },
  item: {
    hidden: {
      y: 60,
      opacity: 0,
      scale: 0.95,
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 0.8,
      },
    },
  },
  cardHover: {
    hover: {
      y: -8,
      scale: 1.02,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25,
      },
    },
  },
  button: {
    hover: {
      scale: 1.05,
      y: -2,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10,
      },
    },
    tap: {
      scale: 0.95,
    },
  },
  fadeInUp: {
    hidden: { y: 40, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  },
  scaleIn: {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  },
  stagger: {
    visible: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  },
  float: {
    float: {
      y: [-10, 10, -10],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  },
  pulse: {
    pulse: {
      scale: [1, 1.05, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  },
};

// Keep your existing App.js structure - it's working fine
export default themeSettings;
